import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Modal, Button, message } from 'antd';
import { InputLabel } from '../../components/Modals/styles';
import TextBlockWithQuillRenderer from '../ComplexDocumentDesign/TextBlockWithQuillRenderer';
import { constructQuillJSON, constructAutoSaveJSON } from '../../utils/TextBlockHelper';
import { getCreativeBlockVariableContract } from '../ComplexDocumentDesign/complexDocumentHelper';
import { withTranslation } from 'react-i18next';
import { EMAIL_SUBJECT_MODAL as trans } from '../../components/Modals/translationValues';
import { COMPLEX_EMAIL_CONTEXT_KEYS, useEmailContext } from '../../context/complexEmailContext';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../context/complexDocumentContext';
import { CONTEXT_KEYS, useArtboardContext } from '../../context/complexArtboardContext';
import { patchEmailSubject } from '../../utils/services/complexEmailAPI';
import { COMPLEX_DOCUMENT_VERSION } from '../../utils/complexDocumentConstants';
import { EMAIL_SUBJECT_CHAR_LIMIT } from '../../utils/constants/appConstants';
import { useVersionContext, VERSION_CONTEXT_KEYS } from '../../context/complexVersionContext';

const NAMESPACE = ['EMAIL_SUBJECT_MODAL']
const ComplexEmailSubjectEditor = (props) => {
  const { t } = props;

  const subjectSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.EMAIL_SUBJECT], []);
  const [subject, emailMethods] = useEmailContext(subjectSelector, 'ComplexEmailSubjectEditor');

  const customVarSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GLOBAL_VARIABLES], []);
  const [globalVariables] = useDocumentContext(
    customVarSelector,
    'ComplexEmailSubjectEditor',
  );

  const subjectContentsRef = useRef(subject);

  const templateIdSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.ID], []);
  const [emailTemplateId] = useDocumentContext(templateIdSelector, 'ComplexEmailSubjectEditor');

  const styleSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES], []);
  const [documentStyle] = useDocumentContext(styleSelector, 'ComplexEmailSubjectEditor');

  const displaySubjectSelector = useCallback(store => store[COMPLEX_EMAIL_CONTEXT_KEYS.OPEN_SUBJECT_MODAL], []);
  const [isVisible] = useEmailContext(displaySubjectSelector, 'ComplexEmailSubjectEditor');

  const lifecycleSelector = useCallback(store => store[VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE], []);
  const [lifeCycle, updateVersionStore] = useVersionContext(lifecycleSelector, `ComplexEmailSubjectEditor`);

  const editableSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable] = useArtboardContext(editableSelector, 'ComplexEmailSubjectEditor');

  const [textEditable, setTextEditable] = useState(false);
  const [isCharLimitExceeded, setIsCharLimitExceeded] = useState(false);

  useEffect(() => {
    subjectContentsRef.current = subject;
  }, [subject]);

  const changeEmailSubject = useCallback(async () => {
    if (subjectContentsRef.current.contents[0] !== '') {
      // TODO : Save subject API integration
      const response = await patchEmailSubject(emailTemplateId, subjectContentsRef.current);
      if (response.statusCode === 3109) {
        message.success(t(trans.UPDATE_EMAIL_SUBJECT_SUCCESSFUL.key, trans.UPDATE_EMAIL_SUBJECT_SUCCESSFUL.value));
        emailMethods.set({
          [COMPLEX_EMAIL_CONTEXT_KEYS.EMAIL_SUBJECT]: Object.assign({}, subjectContentsRef.current, {
            blockId: subjectContentsRef.current.blockId === undefined ? response.data.id : subjectContentsRef.current.blockId
          }),
          [COMPLEX_EMAIL_CONTEXT_KEYS.OPEN_SUBJECT_MODAL]: false
        });
        updateVersionStore({
          [VERSION_CONTEXT_KEYS.LIFECYCLE_TIMELINE]: Object.assign({}, lifeCycle, {
            [COMPLEX_DOCUMENT_VERSION.DRAFT]: new Date()
          })
        });
      } else {
        message.error(t(trans.UPDATE_EMAIL_SUBJECT_FAILURE.key, trans.UPDATE_EMAIL_SUBJECT_FAILURE.value))
      }
    } else {
      message.error(t(trans.EMPTY_EMAIL_SUBJECT_ERROR_MESSAGE.key,trans.EMPTY_EMAIL_SUBJECT_ERROR_MESSAGE.value));
    }
  }, [emailTemplateId, lifeCycle]);

  const onCancel = useCallback(() => {
    emailMethods.set({
      [COMPLEX_EMAIL_CONTEXT_KEYS.OPEN_SUBJECT_MODAL]: false
    })
  }, []);

  const updateSubject = useCallback((contents, undoDiff, redoDiff, cb, ref, op = undefined, newGlobalVariables = []) => { // TODO : Save the subject contents with block variable contract
    const newContents = constructAutoSaveJSON(contents, documentStyle?.fontFamily, documentStyle?.fontSize, newGlobalVariables);
    subjectContentsRef.current = Object.assign({}, subjectContentsRef.current, {
      contents: newContents,
    });
    const variableContract = getCreativeBlockVariableContract(newContents, newGlobalVariables);
    subjectContentsRef.current.variableContract = variableContract.variables;
    console.log("New subject :: ", subjectContentsRef.current);
    cb();
  }, [documentStyle]);

  const getEditorContentLength = useCallback((textLength) => {
    if(textLength > EMAIL_SUBJECT_CHAR_LIMIT) {
      setIsCharLimitExceeded(true);
    } else {
      setIsCharLimitExceeded(false);
    }
  }, []);

  const dummyCallback = useCallback(() => {}, []);
  const isLoading = false;

  if (!isVisible || !editable) return <div />;

  const defaultContent = constructQuillJSON(subject?.contents || [], documentStyle?.fontFamily, documentStyle?.fontSize, globalVariables);

    return (
      <Modal
        width="600px"
        centered
        maskClosable={false}
        visible={isVisible}
        onCancel={onCancel}
        footer={[
          <Button key="back" onClick={onCancel}>
            {t(trans.CANCEL_ENTERED_EMAIL_SUBJECT_BUTTON.key,trans.CANCEL_ENTERED_EMAIL_SUBJECT_BUTTON.value)}
          </Button>,
          <Button key="Send Test Mail" type="primary" loading={isLoading} onClick={changeEmailSubject} disabled={isCharLimitExceeded}>
            {isLoading ? t(trans.SAVING_ENTERED_EMAIL_SUBJECT_BUTTON.key,trans.SAVING_ENTERED_EMAIL_SUBJECT_BUTTON.value) : t(trans.SAVE_ENTERED_EMAIL_SUBJECT_BUTTON.key,trans.SAVE_ENTERED_EMAIL_SUBJECT_BUTTON.value)}
          </Button>,
        ]}
        keyboard={false}
      >
        <InputLabel>
          {t(trans.EMAIL_SUBJECT_LABEL.key,trans.EMAIL_SUBJECT_LABEL.value)}
        </InputLabel>
        <div id="email-subject-toolbar" style={{ visibility: "hidden", height: 0 }} />
        <div id="email-subject-variable" style={{ position: 'relative', top: 10 }} />
        {isVisible && <TextBlockWithQuillRenderer
          defaultContent={defaultContent}
          saveBlock={updateSubject}
          itemUUID={`email-subject`}
          selected={true}
          enableSideBar={dummyCallback}
          enableAI={false}
          toolbarInvisible={true}
          adjustScroll={dummyCallback}
          toolbarDOM={"email-subject-toolbar"}
          variableDOM={"email-subject-variable"}
          textEditable={textEditable}
          setTextEditable={setTextEditable}
          customClassName={'subject-editor'}
          getEditorContentLength={getEditorContentLength}
          isMailSubject={true}
        />}
        {isCharLimitExceeded && 
        <p style={{ color: 'red', padding: '10px' }}>
          {t(trans.MAX_CHAR_EXCEEDED_ERROR.key, trans.MAX_CHAR_EXCEEDED_ERROR.value, { maxCharLength: EMAIL_SUBJECT_CHAR_LIMIT })}
        </p>}
      </Modal>
    );
}

export default withTranslation(NAMESPACE)(ComplexEmailSubjectEditor);